export interface RoomType {
  id: string;
  nameAr: string;
  icon: string;
  category: 'residential' | 'commercial' | 'industrial' | 'educational';
  recommendedLux: number;
  minLux: number;
  maxLux: number;
  description: string;
}

export interface FixturePreset {
  id: string;
  nameAr: string;
  lumens: number;
  watts: number;
  beamAngle: number;
  icon: string;
}

export const ROOM_TYPES: RoomType[] = [
  {
    id: 'bedroom',
    nameAr: 'غرفة نوم',
    icon: '🛏️',
    category: 'residential',
    recommendedLux: 150,
    minLux: 100,
    maxLux: 200,
    description: 'إضاءة هادئة ومريحة للنوم والاسترخاء',
  },
  {
    id: 'living',
    nameAr: 'غرفة معيشة',
    icon: '🛋️',
    category: 'residential',
    recommendedLux: 200,
    minLux: 150,
    maxLux: 300,
    description: 'إضاءة متوازنة للتجمع العائلي',
  },
  {
    id: 'kitchen',
    nameAr: 'مطبخ',
    icon: '🍳',
    category: 'residential',
    recommendedLux: 500,
    minLux: 300,
    maxLux: 750,
    description: 'إضاءة قوية لمساحات العمل',
  },
  {
    id: 'bathroom',
    nameAr: 'حمام',
    icon: '🚿',
    category: 'residential',
    recommendedLux: 300,
    minLux: 200,
    maxLux: 500,
    description: 'إضاءة متوسطة مع تركيز على المرايا',
  },
  {
    id: 'office',
    nameAr: 'مكتب',
    icon: '💼',
    category: 'commercial',
    recommendedLux: 500,
    minLux: 300,
    maxLux: 750,
    description: 'إضاءة مناسبة للعمل والتركيز',
  },
  {
    id: 'study',
    nameAr: 'غرفة دراسة',
    icon: '📚',
    category: 'educational',
    recommendedLux: 500,
    minLux: 300,
    maxLux: 750,
    description: 'إضاءة جيدة للقراءة والدراسة',
  },
  {
    id: 'retail',
    nameAr: 'محل تجاري',
    icon: '🏪',
    category: 'commercial',
    recommendedLux: 750,
    minLux: 500,
    maxLux: 1000,
    description: 'إضاءة قوية لإبراز المنتجات',
  },
  {
    id: 'restaurant',
    nameAr: 'مطعم',
    icon: '🍽️',
    category: 'commercial',
    recommendedLux: 200,
    minLux: 100,
    maxLux: 300,
    description: 'إضاءة دافئة لأجواء مريحة',
  },
  {
    id: 'cafe',
    nameAr: 'كافيه',
    icon: '☕',
    category: 'commercial',
    recommendedLux: 250,
    minLux: 150,
    maxLux: 400,
    description: 'إضاءة مميزة لأجواء مميزة',
  },
  {
    id: 'warehouse',
    nameAr: 'مخزن',
    icon: '📦',
    category: 'industrial',
    recommendedLux: 200,
    minLux: 100,
    maxLux: 300,
    description: 'إضاءة كافية لمساحات التخزين',
  },
  {
    id: 'workshop',
    nameAr: 'ورشة',
    icon: '🔧',
    category: 'industrial',
    recommendedLux: 500,
    minLux: 300,
    maxLux: 750,
    description: 'إضاءة قوية للسلامة والدقة',
  },
  {
    id: 'corridor',
    nameAr: 'ممر',
    icon: '🚪',
    category: 'residential',
    recommendedLux: 100,
    minLux: 50,
    maxLux: 150,
    description: 'إضاءة خفيفة للممرات والمداخل',
  },
  {
    id: 'reception',
    nameAr: 'استقبال',
    icon: '🏢',
    category: 'commercial',
    recommendedLux: 300,
    minLux: 200,
    maxLux: 500,
    description: 'إضاءة احترافية للانطباع الأول',
  },
  {
    id: 'classroom',
    nameAr: 'مدرسة / صف',
    icon: '🏫',
    category: 'educational',
    recommendedLux: 500,
    minLux: 300,
    maxLux: 750,
    description: 'إضاءة مثالية للتعلم والكتابة',
  },
  {
    id: 'hall',
    nameAr: 'صالة',
    icon: '🏛️',
    category: 'commercial',
    recommendedLux: 300,
    minLux: 200,
    maxLux: 500,
    description: 'إضاءة متوازنة للمساحات الكبيرة',
  },
  {
    id: 'other',
    nameAr: 'خيار آخر',
    icon: '✨',
    category: 'commercial',
    recommendedLux: 300,
    minLux: 150,
    maxLux: 500,
    description: 'حدد متطلبات الإضاءة يدوياً',
  },
];

export const FIXTURE_PRESETS: FixturePreset[] = [
  { id: 'led-bulb', nameAr: 'LED Bulb', lumens: 800, watts: 9, beamAngle: 200, icon: '💡' },
  { id: 'downlight', nameAr: 'Downlight', lumens: 1200, watts: 12, beamAngle: 60, icon: '🔦' },
  { id: 'spotlight', nameAr: 'Spotlight', lumens: 700, watts: 7, beamAngle: 35, icon: '🎯' },
  { id: 'led-panel', nameAr: 'LED Panel', lumens: 4000, watts: 40, beamAngle: 120, icon: '⬜' },
  { id: 'linear', nameAr: 'Linear Light', lumens: 3000, watts: 30, beamAngle: 120, icon: '📏' },
  { id: 'pendant', nameAr: 'Pendant', lumens: 1500, watts: 15, beamAngle: 180, icon: '🏮' },
  { id: 'flood', nameAr: 'Flood Light', lumens: 8000, watts: 80, beamAngle: 120, icon: '🔆' },
  { id: 'manual', nameAr: 'إدخال يدوي', lumens: 0, watts: 0, beamAngle: 120, icon: '✏️' },
];

export const DEFAULT_FACTORS = {
  uf: 0.65,
  mf: 0.8,
  reflectance: 0.5,
  mountingHeight: 2.5,
  llf: 0.8,
};

export interface LightingCalculationInput {
  roomType: RoomType;
  length: number;
  width: number;
  ceilingHeight: number;
  workingHeight: number;
  fixtureLumens: number;
  fixtureWatts: number;
  beamAngle: number;
  uf: number;
  mf: number;
  useAdvanced: boolean;
}

export interface LightingCalculationResult {
  area: number;
  targetLux: number;
  requiredLumens: number;
  fixtureCount: number;
  totalLumens: number;
  totalWatts: number;
  estimatedLux: number;
  rows: number;
  cols: number;
  spacingX: number;
  spacingY: number;
  wallOffsetX: number;
  wallOffsetY: number;
  status: 'good' | 'warning' | 'poor';
  statusAr: string;
  luxPercentage: number;
}

export function calculateLighting(input: LightingCalculationInput): LightingCalculationResult {
  const area = input.length * input.width;
  const targetLux = input.roomType.recommendedLux;

  let requiredLumens: number;
  if (input.useAdvanced) {
    requiredLumens = (targetLux * area) / (input.uf * input.mf);
  } else {
    requiredLumens = (targetLux * area) / (DEFAULT_FACTORS.uf * DEFAULT_FACTORS.mf);
  }

  const fixtureCount = Math.ceil(requiredLumens / input.fixtureLumens);
  const totalLumens = fixtureCount * input.fixtureLumens;
  const totalWatts = fixtureCount * input.fixtureWatts;

  const ufUsed = input.useAdvanced ? input.uf : DEFAULT_FACTORS.uf;
  const mfUsed = input.useAdvanced ? input.mf : DEFAULT_FACTORS.mf;
  const estimatedLux = (totalLumens * ufUsed * mfUsed) / area;

  // Distribute fixtures
  const cols = Math.ceil(Math.sqrt(fixtureCount * (input.length / input.width)));
  const rows = Math.ceil(fixtureCount / cols);
  const spacingX = input.length / cols;
  const spacingY = input.width / rows;
  const wallOffsetX = spacingX / 2;
  const wallOffsetY = spacingY / 2;

  const luxPercentage = Math.min(Math.round((estimatedLux / targetLux) * 100), 150);

  let status: 'good' | 'warning' | 'poor';
  let statusAr: string;
  if (estimatedLux >= input.roomType.minLux && estimatedLux <= input.roomType.maxLux * 1.2) {
    status = 'good';
    statusAr = 'الإضاءة مناسبة';
  } else if (estimatedLux < input.roomType.minLux) {
    if (estimatedLux < input.roomType.minLux * 0.7) {
      status = 'poor';
      statusAr = 'الإضاءة غير كافية';
    } else {
      status = 'warning';
      statusAr = 'الإضاءة تحتاج تحسين';
    }
  } else {
    status = 'warning';
    statusAr = 'الإضاءة أعلى من المطلوب';
  }

  return {
    area,
    targetLux,
    requiredLumens,
    fixtureCount,
    totalLumens,
    totalWatts,
    estimatedLux,
    rows,
    cols,
    spacingX,
    spacingY,
    wallOffsetX,
    wallOffsetY,
    status,
    statusAr,
    luxPercentage,
  };
}

export interface InspectionInput {
  roomType: RoomType;
  length: number;
  width: number;
  ceilingHeight: number;
  existingCount: number;
  lumensPerFixture: number;
  uf: number;
  mf: number;
  useAdvanced: boolean;
}

export interface InspectionResult {
  area: number;
  currentLumens: number;
  currentLux: number;
  targetLux: number;
  minLux: number;
  maxLux: number;
  status: 'good' | 'warning' | 'poor' | 'excess';
  statusAr: string;
  luxPercentage: number;
  deficit: number;
  surplus: number;
  additionalFixturesNeeded: number;
  fixturesToRemove: number;
  recommendation: string;
}

export function inspectLighting(input: InspectionInput): InspectionResult {
  const area = input.length * input.width;
  const ufUsed = input.useAdvanced ? input.uf : DEFAULT_FACTORS.uf;
  const mfUsed = input.useAdvanced ? input.mf : DEFAULT_FACTORS.mf;

  const currentLumens = input.existingCount * input.lumensPerFixture;
  const currentLux = (currentLumens * ufUsed * mfUsed) / area;
  const targetLux = input.roomType.recommendedLux;
  const luxPercentage = Math.min(Math.round((currentLux / targetLux) * 100), 200);

  const deficit = Math.max(0, targetLux - currentLux);
  const surplus = Math.max(0, currentLux - targetLux);

  let status: 'good' | 'warning' | 'poor' | 'excess';
  let statusAr: string;
  let recommendation: string;

  const lumensNeededForDeficit = (deficit * area) / (ufUsed * mfUsed);
  const additionalFixturesNeeded = input.lumensPerFixture > 0
    ? Math.ceil(lumensNeededForDeficit / input.lumensPerFixture)
    : 0;

  const excessLux = currentLux - input.roomType.maxLux;
  const lumensToRemove = excessLux > 0 ? (excessLux * area) / (ufUsed * mfUsed) : 0;
  const fixturesToRemove = input.lumensPerFixture > 0
    ? Math.floor(lumensToRemove / input.lumensPerFixture)
    : 0;

  if (currentLux >= input.roomType.minLux && currentLux <= input.roomType.maxLux) {
    status = 'good';
    statusAr = 'الإضاءة مناسبة';
    recommendation = 'الإضاءة الحالية مثالية للمكان. لا تحتاج إلى أي تعديلات.';
  } else if (currentLux < input.roomType.minLux * 0.7) {
    status = 'poor';
    statusAr = 'الإضاءة غير كافية';
    recommendation = `الإضاءة أقل من المستوى المطلوب بشكل كبير. تحتاج إضافة ${additionalFixturesNeeded} مصباح تقريباً للوصول إلى المستوى المناسب.`;
  } else if (currentLux < input.roomType.minLux) {
    status = 'warning';
    statusAr = 'الإضاءة تحتاج تحسين';
    recommendation = `الإضاءة أقل من الموصى به. يُقترح إضافة ${additionalFixturesNeeded} مصباح لتحسين مستوى الإضاءة.`;
  } else {
    status = 'excess';
    statusAr = 'الإضاءة أعلى من المطلوب';
    recommendation = `الإضاءة أعلى من المستوى المطلوب. يمكن تقليل ${fixturesToRemove} مصباح أو استخدام مصابيح بقدرة أقل لتوفير الطاقة.`;
  }

  return {
    area,
    currentLumens,
    currentLux,
    targetLux,
    minLux: input.roomType.minLux,
    maxLux: input.roomType.maxLux,
    status,
    statusAr,
    luxPercentage,
    deficit,
    surplus,
    additionalFixturesNeeded,
    fixturesToRemove,
    recommendation,
  };
}

export function estimateLumensFromWatts(watts: number, type: string): number {
  const efficacyMap: Record<string, number> = {
    'led-bulb': 90,
    'downlight': 95,
    'spotlight': 85,
    'led-panel': 100,
    'linear': 100,
    'pendant': 90,
    'flood': 100,
    'manual': 90,
  };
  const efficacy = efficacyMap[type] || 90;
  return Math.round(watts * efficacy);
}